import { getItemDisplayName } from '../../helpers/adminName';
import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_OBJ } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  ExperienceTemplateMessage,
  IExperienceTemplate,
  ITimelineItem,
  MessageStatus,
  PARTICIPANT_ACTOR_KEY
} from '@dadohr/shared/domainTypes/experience';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import { Button, Dialog, Typography } from '@material-ui/core';
import { isEqual, orderBy } from 'lodash';
import { FileOutput, Mail } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { DialogActionsHeader } from '../../../../components/DialogActionsHeader';
import { DialogContentWithoutPadding } from '../../../../components/DialogContentWithoutPadding';
import { DeactivateItemButton } from '../DeactivateItemButton';
import { useNewUx } from '../../../../services/newUx';
import { DiscardChangesDialog } from '../../../../components/DiscardChangesDialog';
import {
  TimelineItem2,
  TimelineItemTitleWithSuperTile
} from '../../../../components/Timeline';
import {
  TabPanel,
  TimelineDialogTabs,
  useTimelineDialogTabQueryParam
} from '../../../../components/TimelineDialogTabs';
import { useDialogState } from '../../../../hooks/useDialogState';
import { useSnackbar } from '../../../../hooks/useSnackbar';
import { useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { useCurrentUser } from '../../../../services/currentUser';
import { duplicateMessageTemplate } from '../../../../services/experiences/template';
import {
  getFixedStartOrInfinity,
  isValidTiming
} from '../../../../services/experiences/timing';
import { updateDataInDoc } from '../../../../services/firebase';
import { toDenormalizedOrg } from '../../../../services/orgs';
import { useProductAnalytics } from '../../../../services/productAnalytics';
import { interpolateExperienceText } from '../../../../services/set/interpolation';
import { ONE_DAY } from '../../../../services/time';
import { MessageExecutionReportTab } from '../../pages/Details/ExecutionReportTab';
import { TimelineItemNotesTab } from '../../pages/Details/TimelineItemNotesTab';
import { SendTestMessageButton } from '../../pages/Details/SendTest';
import { withCustomizedTimingHint } from '../../pages/Details/TaskTimeline';
import {
  PrimaryHeaderButton,
  Transition
} from '../../pages/TemplateEdit/StepTimeline';
import { ActorDescriptor } from '../Actors';
import {
  getTaskTemplatesWithSameActorAndWithinRange,
  MessageBuilder,
  MessageBuilderReadOnly,
  MessageViewerNewUxShell
} from '../Builders/TaskBuilder';
import { useTokenDefinitionsForExperience } from '../Builders/TaskBuilder/components/BlockSettings/common';
import { useTaskBuilderContext } from '../Builders/TaskBuilder/types';
import { TaskBuilderBlockValidationState } from '../Builders/TaskBuilder/validation';
import { ChannelIcons } from '../Channels';
import { GoToTimelineElementEditButton } from '../GoToTimelineElementEditButton';

// TODO: Remove once we have the export list functionality for report execution implemented
const isExportList = false;
export const updateMessages = (
  d: Doc<IExperienceTemplate>,
  messages: IExperienceTemplate['messages']
) => {
  const actors = Object.keys(d.data.supportingActors);
  return updateDataInDoc(d, {
    messages: {
      ...messages,
      other: orderBy(messages.other || [], [
        (m) => getFixedStartOrInfinity(m.timing),
        (m) =>
          m.actor === PARTICIPANT_ACTOR_KEY
            ? -Infinity
            : actors.indexOf(m.actor)
      ])
    }
  });
};

export const MessageTemplateDialog = ({
  open,
  onClose,
  expTpl,
  value,
  onChange,
  onDelete,
  options,
  mode = 'edit',
  setDuplicatedMessage,
  startTimer,
  getDuration,
  onGoToOtherItem,
  isRoughDraft
}: {
  open: boolean;
  onClose: () => void;
  expTpl: Doc<IExperienceTemplate>;
  value: ExperienceTemplateMessage;
  onChange?: (nextMessage: ExperienceTemplateMessage) => void;
  onDelete?: (msg: ExperienceTemplateMessage) => void;
  options?: {
    disableDateChanges?: boolean;
    disableActorChanges?: boolean;
  };
  mode?: 'create' | 'edit' | 'read' | 'duplicate';
  setDuplicatedMessage?: React.Dispatch<
    React.SetStateAction<ExperienceTemplateMessage | undefined>
  >;
  startTimer: () => number;
  getDuration: () => number;
  onGoToOtherItem?: (otherItem: ITimelineItem) => void;
  isRoughDraft?: boolean;
}) => {
  const { org } = useCurrentUser();
  const { t } = useUnsafeTranslation();
  const { trackEvent } = useProductAnalytics();
  const [v, setV] = useState(value);
  const [previewTab, setPreviewTab] = useTimelineDialogTabQueryParam();
  useEffect(() => {
    setV(value);
  }, [value]);

  useEffect(() => {
    startTimer();
  }, [startTimer]);

  const [
    blockValidation,
    setBlockValidation
  ] = useState<TaskBuilderBlockValidationState>(EMPTY_OBJ);

  const { enqueueSnackbarError, enqueueSnackbarSuccess } = useSnackbar();

  const discardDialog = useDialogState(false);
  const tokens = useTokenDefinitionsForExperience(
    expTpl.data.supportingActors,
    org.customUserFields,
    { orgId: expTpl.data.orgId, experienceTemplateId: expTpl.id }
  );
  const expTplMini = useTaskBuilderContext(expTpl.data);
  const connectedTasks = useMemo(
    () => getTaskTemplatesWithSameActorAndWithinRange(v, expTpl),
    [v, expTpl]
  );

  const close = () => {
    setPreviewTab(null);
    onClose();
  };

  const newUxEnabled = useNewUx().enabled;
  // NEW_UX: the editable modal uses the icon-rail editor shell; the read-only
  // modal becomes the icon-rail message viewer (handled in its own branch below).
  const shellMode = newUxEnabled && mode !== 'read';
  const tryClose = () => {
    if (!isEqual(value, v)) {
      discardDialog.openDialog();
      return;
    }
    close();
  };

  const trackCreateDuplicateClicked = (duplicateId: string) => {
    trackEvent({
      eventType: PAEventType.EXPERIENCE_TPL,
      name: 'Duplicate Timeline Item Button Clicked',
      Explanation:
        'Admin clicks the button to duplicate a timeline item, opening up a Create Timeline Item modal',
      'Experience Template ID': expTpl.id,
      'Original Timeline Item ID': value.id,
      'Original Timeline Item Title': value.subject,
      'Button Location': 'Timeline Item Edit Modal',
      'Duplicate Timeline Item ID': duplicateId,
      'Timeline Item Type': 'Message'
    });
  };

  const trackAddMessage = (duration: number) => {
    trackEvent({
      eventType: PAEventType.EXPERIENCE_TPL,
      name: 'Timeline Item Created',
      Explanation:
        'Admin creates a new timeline item, by clicking the Add button in the Edit Timeline Item modal',
      'Experience Template ID': expTpl.id,
      'Timeline Item ID': v.id,
      'Timeline Item Type': 'Message',
      'Start Date':
        v.timing.type === 'fixed' ? v.timing.start / ONE_DAY + 1 : -1,
      Duplicate: mode === 'duplicate' ? 'true' : 'false',
      'Creation Duration': duration
    });
  };

  const doDuplicateMessage = () => {
    if (!setDuplicatedMessage) return;
    const duplicate = duplicateMessageTemplate(v);
    setDuplicatedMessage({
      ...duplicate,
      subject: `Copy of ${duplicate.subject}`
    });
    trackCreateDuplicateClicked(duplicate.id);
    setTimeout(() => close(), 300);
  };
  const saveButton =
    mode !== 'read' && onChange ? (
      <PrimaryHeaderButton
        newUx={shellMode}
        onClick={() => {
          if (!isRoughDraft) {
            if (!v.subject) {
              enqueueSnackbarError(t('Please provide a subject.'));
              return;
            }
            if (!isValidTiming(v.timing)) {
              enqueueSnackbarError(t('Your start settings are invalid. Please review.'));
              return;
            }
          }
          onChange({
            ...v,
            adminName: v.adminName?.trim() || null
          });
          if (mode === 'create' || mode === 'duplicate') {
            trackAddMessage(Math.round(getDuration() / 1000));
            close();
            enqueueSnackbarSuccess(t(`Added a new message '${v.subject}''`));
          } else {
            enqueueSnackbarSuccess(t(`Saved changes`));
          }
        }}
      >
        {mode === 'create' || mode === 'duplicate'
          ? t('Add Message')
          : t('Save Message')}
      </PrimaryHeaderButton>
    ) : null;
  // Toggle deactivation and persist immediately (bypassing the Save button's
  // validation — you can pause an item without it being complete). Shared by the
  // classic header button and the NEW_UX overflow menu / status pill.
  const doToggleDeactivate = () => {
    if (!onChange) return;
    const next = { ...v, deactivated: !v.deactivated };
    setV(next);
    onChange(next);
    enqueueSnackbarSuccess(
      next.deactivated ? t('Message deactivated') : t('Message reactivated')
    );
  };
  // Primary actions for the item tab (classic header): Duplicate + Save. In NEW_UX,
  // Save is the top-bar primary and Duplicate moves to the overflow menu.
  const itemTabActions = (
    <FlexContainer>
      {mode === 'read' && (
        <GoToTimelineElementEditButton
          expTpl={expTpl}
          type="MESSAGE"
          elementId={value.id}
          title={value.subject}
        />
      )}
      {mode === 'edit' && onChange && (
        <DeactivateItemButton
          deactivated={v.deactivated}
          onConfirm={doToggleDeactivate}
        />
      )}
      {mode === 'edit' && setDuplicatedMessage && (
        <Button variant="outlined" onClick={doDuplicateMessage}>
          {t('Duplicate')}
        </Button>
      )}
      {saveButton}
    </FlexContainer>
  );

  // NEW_UX read-only viewer: the Message/People/Notes tabs become the icon-rail
  // message viewer. Classic (newUx off, or any edit mode) keeps the layout below.
  if (newUxEnabled && mode === 'read') {
    return (
      <Dialog open={open} onClose={close} maxWidth={'xl'} fullWidth>
        {open && (
          <DialogContentWithoutPadding fullHeight>
            <MessageViewerNewUxShell
              value={v}
              expTpl={expTplMini}
              validation={blockValidation}
              tokens={tokens}
              connectedTasks={connectedTasks}
              onCloseDialog={close}
              onGoToOtherItem={onGoToOtherItem}
              editButton={
                <GoToTimelineElementEditButton
                  expTpl={expTpl}
                  type="MESSAGE"
                  elementId={value.id}
                  title={value.subject}
                />
              }
              recipientsPanel={
                <MessageExecutionReportTab
                  tpl={expTpl}
                  msgId={value.id}
                  msgTitle={value.subject}
                  newUx
                />
              }
              notesPanel={
                <TimelineItemNotesTab
                  orgId={expTpl.data.orgId}
                  experienceTemplateId={expTpl.id}
                  itemId={value.id}
                  itemType="MSG"
                />
              }
            />
          </DialogContentWithoutPadding>
        )}
      </Dialog>
    );
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={tryClose}
        maxWidth={'xl'}
        fullWidth
        TransitionComponent={mode === 'duplicate' ? Transition : undefined}
        transitionDuration={300}
      >
        {!shellMode && (
        <DialogActionsHeader
          hasVerticalPadding={mode !== 'read'}
          left={
            mode === 'read' ? (
              <TimelineDialogTabs
                onChange={setPreviewTab}
                label={t('Message')}
                previewTab={previewTab}
                elementId={value.id}
                title={value.subject}
                type="MESSAGE"
                selectedTab="Message"
                experienceTemplateId={expTpl.id}
              />
            ) : (
              <Typography variant="h4" component="h1">
                {mode === 'create' || mode === 'duplicate'
                  ? t('Create Message')
                  : mode === 'edit'
                  ? t('Update Message')
                  : ''}
              </Typography>
            )
          }
          onClose={tryClose}
        >
          {previewTab === 'item' && itemTabActions}
          {isExportList && previewTab === 'people' && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<FileOutput size={16} />}
            >
              Export list
            </Button>
          )}
        </DialogActionsHeader>
        )}
        <DialogContentWithoutPadding fullHeight>
          <TabPanel active={previewTab === 'item'}>
            {mode === 'read' ? (
              <MessageBuilderReadOnly
                expTpl={expTpl}
                value={v}
                validation={blockValidation}
                sendTestMessageButton={
                  <SendTestMessageButton
                    d={expTpl}
                    msgTpl={v}
                    newUx={shellMode}
                  />
                }
                tokens={tokens}
              />
            ) : (
              <MessageBuilder
                expTpl={expTpl}
                value={v}
                onChange={setV}
                options={options}
                validation={blockValidation}
                setValidation={setBlockValidation}
                onDelete={onDelete}
                sendTestMessageButton={
                  <SendTestMessageButton d={expTpl} msgTpl={v} newUx={shellMode} />
                }
                tokens={tokens}
                onGoToOtherItem={onGoToOtherItem}
                newUxPrimaryAction={shellMode ? saveButton : undefined}
                newUxOnDuplicate={
                  shellMode && mode === 'edit' && setDuplicatedMessage
                    ? doDuplicateMessage
                    : undefined
                }
                newUxOnToggleDeactivate={
                  shellMode && mode === 'edit' && onChange
                    ? doToggleDeactivate
                    : undefined
                }
                onCloseDialog={tryClose}
                notesPanel={
                  <TimelineItemNotesTab
                    orgId={expTpl.data.orgId}
                    experienceTemplateId={expTpl.id}
                    itemId={value.id}
                    itemType="MSG"
                  />
                }
              />
            )}
          </TabPanel>
          <TabPanel active={previewTab === 'people'}>
            {previewTab === 'people' && (
              <MessageExecutionReportTab
                tpl={expTpl}
                msgId={value.id}
                msgTitle={value.subject}
              />
            )}
          </TabPanel>
          <TabPanel active={previewTab === 'notes'}>
            <TimelineItemNotesTab
              orgId={expTpl.data.orgId}
              experienceTemplateId={expTpl.id}
              itemId={value.id}
              itemType="MSG"
            />
          </TabPanel>
        </DialogContentWithoutPadding>
      </Dialog>
      <DiscardChangesDialog
        open={discardDialog.dialogOpen}
        onCancel={discardDialog.closeDialog}
        onConfirm={onClose}
      />
    </>
  );
};

export const MessageItem2 = <
  TplContext extends {
    title: string;
    overrides?: IExperienceTemplate['overrides'];
  }
>({
  tplContext,
  message,
  actor,
  variant = 'normal',
  onClick,
  selected,
  onSelect,
  customizedTiming,
  status,
  isMenuActive,
  selectMenu,
  style,
  showActorLabel,
  hideChannelIcons,

  connectUp,
  connectDown,
  connectLeft,
  connectRight
}: {
  tplContext: TplContext;
  message: ExperienceTemplateMessage;
  onClick?: () => void;
  actor: ActorDescriptor | undefined;
  variant?: 'normal' | 'dense' | 'placeholder';

  selected?: boolean;
  onSelect?: (nextSelected: boolean) => void;

  isMenuActive?: boolean;
  customizedTiming?: boolean;

  status?: MessageStatus | null; // pass a status to activate dynamic coloring
  selectMenu?: React.ReactNode;
  style?: React.CSSProperties;
  showActorLabel?: boolean;
  hideChannelIcons?: boolean;

  connectUp?: boolean;
  connectDown?: boolean;
  connectRight?: boolean;
  connectLeft?: 'up' | 'down' | 'upAndDown';
}) => {
  const { org } = useCurrentUser();
  const denormalizedOrg = toDenormalizedOrg(
    org,
    tplContext.overrides?.org || null
  );

  const interpolatedSubject = interpolateExperienceText(
    getItemDisplayName(message),
    {
      experience: {
        vars: {
          title: tplContext.title,
          tasks: {}
        }
      },
      org: {
        vars: {
          name: denormalizedOrg.name
        }
      },
      roles: []
    },
    {
      dateFormat: 'YYYY/MM/DD'
    }
  );

  return (
    <>
      <TimelineItem2
        variant={variant}
        title={
          showActorLabel && actor?.label ? (
            <TimelineItemTitleWithSuperTile
              title={interpolatedSubject}
              superTitle={actor.label}
            />
          ) : (
            interpolatedSubject
          )
        }
        leftColor={actor?.color}
        leftIcon={Mail}
        onClick={onClick}
        right={withCustomizedTimingHint(
          customizedTiming,
          !hideChannelIcons && (
            <ChannelIcons size={16} channels={message.channels} />
          )
        )}
        selected={selected}
        onSelect={onSelect}
        transparent={status === MessageStatus.SKIPPED || message.deactivated}
        selectMenu={selectMenu}
        style={style}
        connectUp={connectUp}
        connectDown={connectDown}
        connectLeft={connectLeft}
        connectRight={connectRight}
      />
    </>
  );
};
